/**
 * API Client for Khaime SDK
 */

import { logPlainError, plainError } from './plain-errors';
import { getCache, CacheKeys, CacheTTL } from './cache';
import type {
  SDKConfig,
  BootstrapResponse,
  Product,
  ProductVariant,
  ProductsResponse,
  ProductQueryOptions,
  CheckoutSession,
  CartApiResponse,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

/**
 * Raw response shape from `GET /sdk/fees/breakdown`. The backend returns
 * fee and total amounts in MAJOR units (e.g. 2.90 for $2.90). The SDK
 * immediately normalizes to minor units in `getFeeBreakdown()` — callers
 * should never need to touch this type directly.
 */
interface RawFeeBreakdownResponse {
  business_settings?: {
    customer_pays_transaction_fee?: boolean;
    payment_gateway?: string;
    currency?: string;
  };
  fee_breakdown?: {
    transaction_fee?: { amount?: number; paid_by?: 'customer' | 'business' };
    total_additional_fees?: number;
  };
  customer_totals?: {
    pays_subtotal?: number;
    pays_additional_fees?: number;
    pays_total?: number;
  };
}

/**
 * Normalized fee breakdown — all monetary amounts are in MINOR units
 * (cents/kobo/pesewas), matching the convention used throughout the SDK
 * (Product.price, CartItem.price, CartManager.getSubtotalMinor, etc.).
 */
export interface FeeBreakdownMinor {
  customerPaysFees: boolean;
  gateway: string;
  currency: string;
  transactionFeeMinor: number;
  totalAdditionalFeesMinor: number;
  customerPaysSubtotalMinor: number;
  customerPaysTotalMinor: number;
}

function toMinor(major: number | undefined | null): number {
  if (major == null || Number.isNaN(major)) return 0;
  return Math.round(Number(major) * 100);
}

/**
 * Detect API base URL from script source or hostname.
 *
 * IMPORTANT: We match the SDK script by FILENAME, not by substring. An
 * earlier version used `script.src.includes('khaime')` which accidentally
 * matched any URL containing the substring "khaime" anywhere — including
 * hostnames like `woocommerce-khaime.local` where every WP-bundled script
 * (jQuery, theme, plugins) has "khaime" in the URL. The detection would
 * then enter the branch on the FIRST such script (jquery, etc.), see that
 * the hostname doesn't contain "khaimedev", and return the prod base URL —
 * never reaching the actual SDK script that comes later in the list.
 *
 * The fix: only consider scripts whose filename (last URL segment) starts
 * with "khaime", which matches khaime.js, khaime.iife.js, khaime.esm.js,
 * khaime.min.js etc. but NOT /wp-includes/jquery.min.js even on a hostname
 * like woocommerce-khaime.local.
 *
 * Additionally, when multiple SDK scripts are loaded (e.g. several Khaime
 * WP plugins on the same page), prefer dev over prod so a single
 * dev-environment shortcode wins for the whole page rather than losing to
 * whichever plugin happened to enqueue first.
 */
function detectApiBase(): string {
  if (typeof window !== 'undefined' && (window as any).KHAIME_API_OVERRIDE) {
    return (window as any).KHAIME_API_OVERRIDE;
  }

  if (typeof document !== 'undefined') {
    const scripts = document.getElementsByTagName('script');
    let prodFallback: string | null = null;
    for (const script of scripts) {
      if (!script.src) continue;
      // Extract filename (last path segment, query string stripped)
      let filename: string;
      try {
        filename = new URL(script.src).pathname.split('/').pop() || '';
      } catch {
        continue;
      }
      if (!filename.startsWith('khaime')) continue;
      try {
        const url = new URL(script.src);
        if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
          // Localhost wins outright — local dev takes precedence over everything
          return `${url.protocol}//${url.host}/api/v1`;
        }
        if (url.hostname.includes('khaimedev')) {
          // Dev wins over prod — return immediately
          return 'https://api.khaimedev.com/api/v1';
        }
        // Prod is the fallback if no dev/local script is found
        prodFallback = 'https://api.khaime.com/api/v1';
      } catch {
        // Invalid URL — skip
      }
    }
    if (prodFallback) return prodFallback;
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}${port ? ':' + port : ''}/api/v1`;
    }
    if (hostname.includes('khaimedev')) {
      return 'https://api.khaimedev.com/api/v1';
    }
  }

  return 'https://api.khaime.com/api/v1';
}

/**
 * API Client
 */
/** The initialize answer, in the shape the checkout components read. */
export function normalizeCheckoutSession(raw: any, productId: number): CheckoutSession {
  return {
    sessionToken: raw?.session_token ?? raw?.sessionToken,
    amount: Number(raw?.payment?.amount ?? raw?.amount ?? 0),
    currency: raw?.payment?.currency ?? raw?.currency ?? 'USD',
    productId,
    productTitle: raw?.product?.name ?? raw?.productTitle ?? '',
    paymentGateway: raw?.sdk_config?.payment_gateway ?? raw?.paymentGateway,
    merchantBranding: {
      name: raw?.merchant?.name ?? '',
      logo: raw?.merchant?.logo ?? undefined,
      brandColor: raw?.merchant?.brand_color ?? undefined,
    },
    expiresAt: raw?.expires_at ?? raw?.expiresAt ?? '',
  };
}

export class ApiClient {
  private baseUrl: string;
  private merchantId: string | number;
  private apiKey?: string;
  private sandbox: boolean;
  private cache = getCache();
  private debug: boolean;

  constructor(options: {
    merchantId: string | number;
    apiKey?: string;
    sandbox?: boolean;
    apiBase?: string;
    debug?: boolean;
  }) {
    this.merchantId = options.merchantId;
    this.apiKey = options.apiKey;
    this.sandbox = options.sandbox ?? false;
    this.baseUrl = options.apiBase ?? detectApiBase();
    this.debug = options.debug ?? false;
  }

  /**
   * Make an API request (60s safety-net timeout)
   */
  async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Merchant-ID': String(this.merchantId),
      ...(options.headers as Record<string, string>),
    };

    if (this.apiKey) headers['X-API-Key'] = this.apiKey;
    if (this.sandbox) headers['X-SDK-Sandbox'] = 'true';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60_000);

    try {
      const response = await fetch(url, { ...options, headers, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        let serverMessage = response.statusText;
        let code: string | undefined;
        try {
          const body = await response.json();
          serverMessage = body.message || body.error || serverMessage;
          code = body.error_code;
        } catch { /* not JSON */ }
        const plain = plainError(response.status, code, serverMessage);
        logPlainError(plain);
        const err = new Error(`HTTP ${response.status}: ${plain}`) as Error & { code?: string; status?: number };
        err.code = code;
        err.status = response.status;
        throw err;
      }

      const json: ApiResponse<T> = await response.json();
      if (!json.success && json.error) throw new Error(json.error);

      return json.data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === 'AbortError') {
        logPlainError('Khaime took too long to answer. Check the internet connection and reload the page.');
        throw new Error(`Request timed out: ${path}`);
      }
      if (error instanceof TypeError) {
        // fetch rejects with a TypeError when the request never got an answer.
        logPlainError(
          'Could not reach Khaime. Check the internet connection. If it keeps happening, check the <script src=".../sdk/v2/khaime.js"> line is in the page head.'
        );
      }
      throw error;
    }
  }

  async get<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T>(path: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body: JSON.stringify(body) });
  }

  // -------------------------------------------------------
  // Domain Methods (with caching)
  // -------------------------------------------------------

  async bootstrap(): Promise<BootstrapResponse> {
    const cacheKey = CacheKeys.bootstrap(this.merchantId, this.sandbox);
    return this.cache.getOrFetch(
      cacheKey,
      async () => {
        const raw: any = await this.get('/sdk/storefront/bootstrap');
        return this.normalizeBootstrapResponse(raw);
      },
      CacheTTL.BOOTSTRAP
    ) as Promise<BootstrapResponse>;
  }

  async getConfig(sdkType: string = 'storefront'): Promise<SDKConfig> {
    const cacheKey = CacheKeys.config(this.merchantId, sdkType);
    return this.cache.getOrFetch(
      cacheKey,
      () => this.get<SDKConfig>(`/sdk/config/${sdkType}/published`),
      CacheTTL.CONFIG
    ) as Promise<SDKConfig>;
  }

  async getProducts(
    options: Omit<ProductQueryOptions, 'merchantId'> = {},
    { skipCache = false }: { skipCache?: boolean } = {}
  ): Promise<ProductsResponse> {
    const params = new URLSearchParams();
    if (options.currency) params.set('currency', options.currency);
    if (options.productIds) params.set('product_ids', options.productIds);
    if (options.category) params.set('category', options.category);
    // Real ProductCategory filters (separate from `category`, which the
    // backend treats as an alias for product_type).
    if (options.categoryId !== undefined && options.categoryId !== '') {
      params.set('category_id', String(options.categoryId));
    }
    if (options.categorySlug) params.set('category_slug', options.categorySlug);
    if (options.type) params.set('type', options.type);
    if (options.sort) params.set('sort', options.sort);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    const path = `/sdk/storefront/products${query ? `?${query}` : ''}`;
    const cacheKey = CacheKeys.products(this.merchantId, options.currency || 'default', options);

    if (skipCache) this.cache.delete(cacheKey);

    const raw: any = await this.cache.getOrFetch(
      cacheKey,
      () => this.get<any>(path),
      CacheTTL.PRODUCTS
    );

    return this.normalizeProductsResponse(raw);
  }

  async getProduct(productId: number, currency?: string): Promise<Product> {
    const params = currency ? `?currency=${currency}` : '';
    const path = `/sdk/storefront/products/${productId}${params}`;
    const cacheKey = CacheKeys.product(this.merchantId, productId, currency || 'default');

    const raw: any = await this.cache.getOrFetch(
      cacheKey,
      () => this.get<any>(path),
      CacheTTL.PRODUCT
    );

    return this.normalizeProductDetail(raw);
  }

  // -------------------------------------------------------
  // Checkout & Cart
  // -------------------------------------------------------

  async getCheckoutSession(sessionToken: string): Promise<{
    session_id: string;
    payment_gateway: 'stripe' | 'paystack';
    stripe?: { publishable_key: string; client_secret: string };
    paystack?: { public_key: string; access_code: string; reference: string };
    payment: { amount: number; currency: string };
  }> {
    return this.get(`/sdk/checkout/session/${sessionToken}`);
  }

  async initCheckout(options: {
    productId: number;
    variantId?: number;
    quantity?: number;
    customerEmail?: string;
    customerName?: string;
    successUrl?: string;
    cancelUrl?: string;
    currency?: string;
    subscriptionFrequencyKey?: string;
  }): Promise<CheckoutSession> {
    // The endpoint takes product_id and a customer with email and name, and
    // answers in snake_case. Sending the options as they were (productId, no
    // customer) failed every Buy now with "product_id is required".
    const raw: any = await this.post<any>('/sdk/checkout/initialize', {
      product_id: options.productId,
      quantity: options.quantity,
      currency: options.currency,
      subscription_frequency_key: options.subscriptionFrequencyKey,
      success_url: options.successUrl,
      cancel_url: options.cancelUrl,
      customer: { email: options.customerEmail, full_name: options.customerName },
    });
    return normalizeCheckoutSession(raw, options.productId);
  }

  async addToCart(
    cartId: string,
    item: {
      product_id: number;
      quantity: number;
      variant_id?: number | string;
      sub_variant_id?: number | string;
      ticket_id?: number;
      tickets?: Array<{ ticket_id: number; quantity: number }>;
      currency: string;
    }
  ): Promise<CartApiResponse> {
    return this.post<CartApiResponse>('/sdk/storefront/cart/items', { cart_id: cartId, ...item });
  }

  async syncCart(
    cartId: string,
    items: Array<{
      product_id: number;
      quantity: number;
      variant_id?: number | string;
      sub_variant_id?: number | string;
      ticket_id?: number;
      tickets?: Array<{ ticket_id: number; quantity: number }>;
      added_at?: string;
    }>,
    currency: string
  ): Promise<CartApiResponse & { stock_warnings?: Array<{ product_id: number; requested: number; available: number }> }> {
    return this.request('/sdk/storefront/cart/sync', {
      method: 'PUT',
      body: JSON.stringify({ cart_id: cartId, items, currency }),
    });
  }

  async getCart(cartId: string, currency?: string): Promise<CartApiResponse> {
    const params = currency ? `?currency=${currency}` : '';
    return this.get<CartApiResponse>(`/sdk/storefront/cart/${cartId}${params}`);
  }

  /**
   * Fetch Khaime's fee breakdown for a given amount + currency, normalized
   * to MINOR units on the response so callers don't have to remember that
   * the backend ships amounts in major units.
   *
   * Used to render a "Transaction Fee" row in cart / checkout totals when
   * the merchant has customer_pays_transaction_fee enabled. Safe to call
   * often — callers should cache per (amountMinor, currency) themselves.
   *
   * @param amountMinor      Amount in minor units (cents, kobo).
   * @param currency         ISO currency code the customer will pay in.
   * @param customerCountry  Optional ISO-2 country code (billing/shipping).
   */
  async getFeeBreakdown(
    amountMinor: number,
    currency: string,
    customerCountry?: string
  ): Promise<FeeBreakdownMinor | null> {
    const params = new URLSearchParams({
      amount: String(amountMinor),
      currency,
    });
    if (customerCountry) {
      params.append('customer_country', customerCountry);
    }

    const response = await this.get<{ data?: RawFeeBreakdownResponse }>(
      `/sdk/fees/breakdown?${params.toString()}`
    );
    const data = response?.data;
    if (!data) return null;

    return {
      customerPaysFees: !!data.business_settings?.customer_pays_transaction_fee,
      gateway: data.business_settings?.payment_gateway || '',
      currency: data.business_settings?.currency || currency,
      // Backend returns major units — convert at the boundary.
      transactionFeeMinor: toMinor(data.fee_breakdown?.transaction_fee?.amount),
      totalAdditionalFeesMinor: toMinor(data.fee_breakdown?.total_additional_fees),
      customerPaysSubtotalMinor: toMinor(data.customer_totals?.pays_subtotal),
      customerPaysTotalMinor: toMinor(data.customer_totals?.pays_total),
    };
  }

  async updateCartItem(
    cartId: string,
    productId: number,
    data: { quantity: number; variant_id?: number },
    currency?: string
  ): Promise<CartApiResponse> {
    const params = currency ? `?currency=${currency}` : '';
    return this.request(`/sdk/storefront/cart/${cartId}/items/${productId}${params}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async removeCartItem(
    cartId: string,
    productId: number,
    variantId?: number,
    currency?: string
  ): Promise<CartApiResponse> {
    const params = new URLSearchParams();
    if (variantId) params.set('variant_id', String(variantId));
    if (currency) params.set('currency', currency);
    const query = params.toString();
    return this.request(`/sdk/storefront/cart/${cartId}/items/${productId}${query ? `?${query}` : ''}`, {
      method: 'DELETE',
    });
  }

  async clearCart(cartId: string): Promise<CartApiResponse> {
    return this.request(`/sdk/storefront/cart/${cartId}`, { method: 'DELETE' });
  }

  // -------------------------------------------------------
  // Shipping & Payment
  // -------------------------------------------------------

  async validateCartEarly(options: {
    cart: Array<{
      product_id: number;
      quantity: number;
      price?: number;
      has_variation?: boolean;
      main_variant?: string | number;
      least_sub_variant_id?: string | number;
    }>;
    customer: { email: string; name?: string; phone?: string };
    currency: string;
  }): Promise<{ cart_unique_id: string; abandonment_captured: boolean; mode: 'early_capture' }> {
    return this.post('/customer/validate-shipping-and-discounts', {
      capture_abandonment_early: true,
      cart: options.cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        has_variation: item.has_variation ?? false,
        main_variant: item.main_variant,
        least_sub_variant_id: item.least_sub_variant_id,
      })),
      delivery_details: {
        address: [{
          email: options.customer.email,
          name: options.customer.name,
          phone_number: options.customer.phone,
        }],
      },
      currency: options.currency,
    });
  }

  async validateShippingAndDiscounts(options: {
    cart: Array<{
      product_id: number;
      quantity: number;
      has_variation: boolean;
      main_variant?: string | number;
      least_sub_variant_id?: string | number;
      price: number;
      product_image: string;
      product_variant_data: string;
      subscription_frequency_key?: string;
    }>;
    delivery_details: {
      save_for_future_checkout: boolean;
      store_pick_up: boolean;
      national_delivery: boolean;
      international_delivery: boolean;
      address: Array<{
        name?: string;
        address: string;
        city: string;
        state: string;
        zip_code: string;
        country: string;
        country_code?: string;
        email?: string;
        phone_number?: string;
      }>;
    };
    currency: string;
    coupons?: string[];
    cart_unique_id?: string;
    use_points?: boolean;
    points_to_use?: number;
    selected_shipping_method?: 'easypost' | 'uber_direct' | 'standard';
    uber_direct_quote_id?: string;
  }): Promise<{
    cart_unique_id: string;
    out_of_stock: boolean;
    out_of_stock_items?: Array<{ product_id: number; title: string }>;
    all_have_total_amount: boolean;
    all_have_quantity: boolean;
    total_quantity: number;
    total_sum: number;
    total_sum_with_shipping_fee: number;
    shipping_fee: number;
    is_shipping_fee: boolean;
    shipping_rates?: Array<{
      id: string;
      carrier: string;
      service: string;
      rate: number;
      estimated_delivery_days?: number;
    }>;
    cart_details: Array<{
      product_id: number;
      title: string;
      quantity: number;
      price: number;
      total: number;
    }>;
    pricing_summary: {
      cart_subtotal: number;
      total_discounts: number;
      cart_total_after_discounts: number;
      points_discount?: number;
      cart_total_after_points?: number;
      shipping_cost: number;
      tax_amount: number;
      cart_final_total: number;
      final_total_with_fees: number;
      currency: string;
      transaction_fee_amount?: number;
      platform_fee_amount?: number;
      customer_pays_transaction_fees?: boolean;
      applied_coupons?: Array<{ code: string; discount: number }>;
    };
  }> {
    return this.post('/customer/validate-shipping-and-discounts', options);
  }

  async createPaymentIntent(options: {
    amount: number;
    currency: string;
    customer: { email: string; first_name?: string; last_name?: string; country?: string };
    product_type: 'digital' | 'physical_product' | 'live_event' | 'subscription' | 'service' | 'course';
    product_name?: string;
    product_id?: number;
    cart_unique_id?: string;
    subscription_frequency_key?: string;
    tickets?: Array<{ ticket_id: number; quantity: number }>;
    metadata?: Record<string, any>;
  }): Promise<{
    intent_id: string;
    client_secret?: string;
    publishable_key?: string;
    stripe_account_id?: string;
    access_code?: string;
    authorization_url?: string;
    amount: number;
    currency: string;
    status: string;
    payment_gateway: 'stripe' | 'paystack';
    transaction_id?: number;
  }> {
    const initBody: Record<string, any> = {
      product_id: options.product_id,
      amount: options.amount,
      currency: options.currency,
      customer: {
        email: options.customer.email,
        first_name: options.customer.first_name,
        last_name: options.customer.last_name,
      },
      product_type: options.product_type,
      product_name: options.product_name,
    };

    if (options.subscription_frequency_key) initBody.subscription_frequency_key = options.subscription_frequency_key;
    if (options.tickets) initBody.tickets = options.tickets;

    const initResult = await this.post<{
      session_token: string;
      payment: { amount: number; currency: string };
      sdk_config: { payment_gateway: 'stripe' | 'paystack' };
    }>('/sdk/checkout/initialize', initBody);

    const sessionData = await this.get<{
      session_id: string;
      payment_gateway: 'stripe' | 'paystack';
      stripe?: { publishable_key: string; client_secret: string; account_id?: string };
      paystack?: { public_key: string; access_code: string; reference: string };
      payment: { amount: number; currency: string };
    }>(`/sdk/checkout/session/${initResult.session_token}`);

    this.log('Payment session:', {
      gateway: sessionData.payment_gateway,
      amount: sessionData.payment?.amount,
      currency: sessionData.payment?.currency,
    });

    return {
      intent_id: initResult.session_token,
      client_secret: sessionData.stripe?.client_secret,
      publishable_key: sessionData.stripe?.publishable_key,
      stripe_account_id: sessionData.stripe?.account_id,
      access_code: sessionData.paystack?.access_code,
      authorization_url: undefined,
      amount: sessionData.payment.amount,
      currency: sessionData.payment.currency,
      status: 'pending',
      payment_gateway: sessionData.payment_gateway,
    };
  }

  async createCartPaymentIntent(options: {
    product_id: number;
    product_title: string;
    product_type: string;
    currency: string;
    order_total_charge: number;
    cart_unique_id: string;
    cart: Array<{
      product_id: number;
      quantity: number;
      has_variation: boolean;
      main_variant?: string;
      least_sub_variant_id?: string;
      price: number;
      product_image?: string;
      product_variant_data?: string;
      product_title?: string;
      product_thumbnail?: string;
      subscription_frequency_key?: string;
    }>;
    delivery_details: {
      save_for_future_checkout: boolean;
      store_pick_up: boolean;
      national_delivery: boolean;
      international_delivery: boolean;
      address: Array<{
        name?: string;
        address: string;
        city: string;
        state: string;
        zip_code: string;
        country: string;
        country_code: string;
        email?: string;
        phone_number?: string;
      }>;
    };
    customer: { email: string; first_name?: string; last_name?: string; phone?: string };
    coupons?: string[];
    subscription_frequency_key?: string;
  }): Promise<{
    secret?: string;
    publishable_key?: string;
    data?: { stripe_account_id?: string; [key: string]: any };
    access_code?: string;
    reference?: string;
    amount_to_pay: number;
    currency: string;
    payment_gateway: 'stripe' | 'paystack';
    transaction_id?: number;
  }> {
    const hasCoupons = (options.coupons?.length ?? 0) > 0;
    const userCountry = options.delivery_details.address[0]?.country_code;

    const body: Record<string, unknown> = {
      // Required so the payment-intent service hits its SDK branch for
      // Paystack (calls the Paystack Initialize API directly and returns
      // `access_code` + `authorization_url`). Without this the endpoint
      // falls through to the "regular frontend" inline-SDK path, which
      // returns only metadata and leaves the cart-checkout modal with
      // nothing to redirect to — hence the spurious "payment success".
      payment_source: 'sdk',
      email: options.customer.email,
      ...(options.customer.first_name ? { first_name: options.customer.first_name } : {}),
      ...(options.customer.last_name ? { last_name: options.customer.last_name } : {}),
      ...(userCountry ? { user_country: userCountry } : {}),
      user_phone_number:
        options.customer.phone || options.delivery_details.address[0]?.phone_number || '',
      payment_gateway: this.detectPaymentGateway(options.currency),
      payment_type: (options.subscription_frequency_key || options.cart.some((i: any) => i.subscription_frequency_key)) ? 'subscription' : 'one',
      currency: options.currency,
      product_type: options.product_type,
      product_title: options.product_title,
      is_coupon_used: hasCoupons,
      is_second_time_payment: false,
      ...(hasCoupons && options.coupons?.[0] ? { coupon_name_code: options.coupons[0] } : {}),
      payment_by: 'customer' as const,
      mentorship_available_time_ids: [],
      consultation_times: [],
      order_total_charge: options.order_total_charge,
      cart: options.cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        has_variation: item.has_variation,
        ...(item.main_variant ? { main_variant: item.main_variant } : {}),
        ...(item.least_sub_variant_id ? { least_sub_variant_id: item.least_sub_variant_id } : {}),
        price: item.price,
        product_image: item.product_image || '',
        ...(item.product_variant_data ? { product_variant_data: item.product_variant_data } : {}),
        ...(item.product_title ? { product_title: item.product_title } : {}),
        ...(item.product_thumbnail ? { product_thumbnail: item.product_thumbnail } : {}),
        ...(item.subscription_frequency_key ? { subscription_frequency_key: item.subscription_frequency_key } : {}),
      })),
      coupons: options.coupons || [],
      delivery_details: options.delivery_details,
      cart_unique_id: options.cart_unique_id,
      is_live: !this.sandbox,
    };

    const raw = await this.post<any>(
      `/student/payment-intent/${options.product_id}`,
      body
    );

    // The student payment-intent endpoint doesn't echo `payment_gateway` at
    // the top level — Paystack responses carry it nested in the `fees` JSON
    // string, Stripe responses leave it off entirely. The cart-checkout
    // modal branches on this field, so infer it here from the response
    // shape (access_code → Paystack, client_secret → Stripe) and fall back
    // to currency-based detection.
    if (!raw.payment_gateway) {
      if (raw.access_code || raw.authorization_url) {
        raw.payment_gateway = 'paystack';
      } else if (raw.secret || raw.client_secret) {
        raw.payment_gateway = 'stripe';
      } else {
        raw.payment_gateway = this.detectPaymentGateway(options.currency);
      }
    }

    return raw;
  }

  // -------------------------------------------------------
  // Utilities
  // -------------------------------------------------------

  preload(): void {
    const run = () => {
      this.bootstrap().catch(() => {});
      this.getConfig().catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run);
    } else {
      setTimeout(run, 100);
    }
  }

  invalidateCache(pattern?: string): void {
    if (pattern) {
      this.cache.invalidatePattern(pattern);
    } else {
      this.cache.invalidatePattern(`config:${this.merchantId}`);
      this.cache.invalidatePattern(`bootstrap:${this.merchantId}`);
      this.cache.invalidatePattern(`products:${this.merchantId}`);
    }
  }

  private detectPaymentGateway(currency: string): 'stripe' | 'paystack' {
    const PAYSTACK_CURRENCIES = ['NGN', 'GHS', 'ZAR', 'KES'];
    return PAYSTACK_CURRENCIES.includes(currency.toUpperCase()) ? 'paystack' : 'stripe';
  }

  private log(...args: unknown[]): void {
    if (this.debug) console.log('[Khaime SDK]', ...args);
  }

  // -------------------------------------------------------
  // Response Normalization
  // -------------------------------------------------------

  private normalizeBootstrapResponse(raw: any): BootstrapResponse {
    // Fast path: response is already client-shaped. Still normalize each
    // currency entry so downstream code can rely on `countryCode`.
    if (Array.isArray(raw.currencies) && raw.detectedCurrency !== undefined) {
      const currencies = raw.currencies.map((c: any) => ({
        code: c.code,
        symbol: c.symbol,
        name: c.name,
        countryCode: c.country_code || c.countryCode || null,
      }));
      return { ...raw, currencies } as BootstrapResponse;
    }

    const currenciesObj = raw.currencies || {};
    const currenciesRaw = Array.isArray(currenciesObj)
      ? currenciesObj
      : Array.isArray(currenciesObj.supported)
        ? currenciesObj.supported
        : [];

    // Normalize each entry so `countryCode` is always available to
    // consumers that want to render flags or country-scoped labels.
    // Backend returns `country_code`; accept either shape.
    const currencies = currenciesRaw.map((c: any) => ({
      code: c.code,
      symbol: c.symbol,
      name: c.name,
      countryCode: c.country_code || c.countryCode || null,
    }));

    return {
      merchant: {
        id: raw.merchant?.id,
        name: raw.merchant?.name,
        slug: raw.merchant?.slug,
        logo: raw.merchant?.logo,
        brandColor: raw.merchant?.brand_color || raw.merchant?.brandColor,
      },
      currencies,
      defaultCurrency: currenciesObj.baseline || raw.defaultCurrency || 'USD',
      detectedCurrency:
        currenciesObj.customer_detected ||
        raw.customer?.detected_currency ||
        raw.detectedCurrency ||
        'USD',
      multicurrencyEnabled: currenciesObj.multicurrency_enabled === true,
      storeCurrency: currenciesObj.business || currenciesObj.settlement || undefined,
      shopperCurrency: currenciesObj.customer || undefined,
      productTypes: raw.product_types || raw.productTypes || [],
      config: raw.config,
      storePulse: raw.store_pulse?.key
        ? { key: raw.store_pulse.key, scriptSrc: raw.store_pulse.script_src, apiBase: raw.store_pulse.api_base }
        : null,
    };
  }

  private normalizeProductsResponse(raw: any): ProductsResponse {
    if (raw.hasMore !== undefined && raw.total !== undefined && !raw.pagination) {
      return raw as ProductsResponse;
    }

    const products = (raw.products || []).map((p: any) => this.normalizeListProduct(p));
    const pagination = raw.pagination || {};

    return {
      products,
      total: pagination.count ?? raw.total ?? products.length,
      page:
        pagination.offset != null && pagination.limit
          ? Math.floor(pagination.offset / pagination.limit) + 1
          : (raw.page ?? 1),
      limit: pagination.limit ?? raw.limit ?? 20,
      hasMore: pagination.has_more ?? raw.hasMore ?? false,
    };
  }

  private normalizeListProduct(p: any): Product {
    const price = this.extractPrice(p);
    const slashPrice = typeof p.slash_price === 'number' && p.slash_price > 0
      ? p.slash_price
      : p.pricing?.original_local?.amount ?? p.slash_price;

    return {
      id: p.id,
      title: p.title,
      description: p.description,
      price,
      formatted_price: p.formatted_price ?? p.pricing?.local?.formatted ?? p.pricing?.base?.formatted,
      slash_price: slashPrice,
      formatted_slash_price: p.pricing?.original_local?.formatted ?? p.formatted_slash_price,
      is_slash_price: p.is_slash_price ?? (slashPrice != null && slashPrice < price),
      currency: p.currency || p.pricing?.local?.currency || p.pricing?.base?.currency || 'USD',
      type: p.type,
      thumbnail: p.thumbnail,
      images: p.images,
      is_live: p.is_live ?? true,
      stock: p.type === 'live_event' ? undefined : (p.stock ?? p.total_quantity ?? p.capacity?.available),
      variants: this.normalizeVariants(p),
      subscription_options: p.subscription_options || p.subscription || undefined,
      rating: p.rating,
      reviews_count: p.reviews_count,
      slug: p.slug,
    };
  }

  private extractPrice(p: any): number {
    if (p.currency_conversion_details?.price > 0) return p.currency_conversion_details.price;
    if (typeof p.price === 'number' && p.price > 0) return p.price;
    if (p.pricing?.local?.amount > 0) return p.pricing.local.amount;
    if (p.pricing?.base?.amount > 0) return p.pricing.base.amount;
    return 0;
  }

  private normalizeProductDetail(raw: any): Product {
    if (typeof raw.price === 'number' && !raw.product) return raw as Product;

    const p = raw.product || raw;
    const pricing = p.pricing;

    return {
      id: p.id,
      title: p.title,
      slug: p.slug,
      type: p.type,
      description: p.description || p.additional_description,
      // Same meaning as a listed product: price is the original, slash_price
      // what the shopper pays when on sale. pricing.local is the amount paid,
      // so on a sale it is the slash price, not the price (the cart charged the
      // original for products loaded one at a time).
      price: pricing
        ? (pricing.original_local?.amount ?? pricing.local?.amount ?? pricing.base?.amount ?? 0)
        : (p.price ?? 0),
      formatted_price: p.formatted_price ?? pricing?.local?.formatted ?? pricing?.base?.formatted,
      slash_price: pricing ? (pricing.is_discounted ? pricing.local?.amount : undefined) : p.slash_price,
      formatted_slash_price: p.formatted_slash_price ?? pricing?.original_local?.formatted,
      is_slash_price: pricing?.is_discounted ?? p.is_slash_price ?? false,
      currency: pricing?.local?.currency ?? pricing?.base?.currency ?? p.currency ?? 'USD',
      thumbnail: p.thumbnail,
      images: p.images,
      is_live: true,
      stock: p.type === 'live_event' ? undefined : (p.capacity?.available ?? p.stock ?? p.total_quantity),
      variants: this.normalizeVariants(p),
      subscription_options: p.subscription_options || p.subscription || undefined,
      event: p.event ? {
        ...p.event,
        tickets: (p.event.tickets || []).map((t: any) => ({
          id: t.id,
          name: t.name || t.title,
          description: t.description,
          price: t.price,
          currency: t.currency,
          quantity_remaining: t.quantity_remaining ?? t.quantity ?? 0,
          ticket_deadline: t.ticket_deadline,
          is_sold_out: t.is_sold_out ?? (t.quantity_remaining ?? t.quantity ?? 0) <= 0,
        })),
      } : undefined,
      rating: p.rating,
      reviews_count: p.reviews_count,
    };
  }

  /**
   * The options a shopper can pick, each at its FULL unit price.
   *
   * The API's `variants` list already carries full prices (base or sale price
   * plus add-ons) in the shopper's currency, with checkout's ids. Older API
   * responses only had raw `product_variation`, whose `variation_add_price` is
   * the add-on alone; that is never an option's price, so the fallback adds
   * it to the product's price (Lovable QA, 3 Nov 2026: Purple at KES 1,352).
   */
  private normalizeVariants(p: any): ProductVariant[] | undefined {
    if (Array.isArray(p?.variants)) {
      if (p.variants.length === 0) return undefined;
      return p.variants.map((v: any) => ({
        id: v.id,
        name: v.name,
        price: v.price ?? 0,
        formatted_price: v.formatted_price ?? null,
        variation_id: v.variation_id ?? v.id,
        sub_variant_id: v.sub_variant_id ?? null,
        sold_out: Boolean(v.sold_out),
        stock: v.stock ?? null,
      }));
    }

    const variation = p?.product_variation;
    if (!variation?.variations?.length) return undefined;
    const onSale = p.is_slash_price && typeof p.slash_price === 'number' && p.slash_price > 0;
    const base = Number(onSale ? p.slash_price : p.price) || 0;
    const out: ProductVariant[] = [];
    for (const v of variation.variations) {
      const label = v.variant_label || v.name || v.title || v.variation_name || '';
      const add = Number(v.variation_add_price) || 0;
      const subs: any[] = v.sub_variation?.sub_variants || [];
      if (subs.length) {
        for (const sub of subs) {
          out.push({
            id: sub.id,
            name: [label, sub.variant_label || sub.name].filter(Boolean).join(' · '),
            price: base + add + (Number(sub.subvariant_add_price) || 0),
            variation_id: v.id,
            sub_variant_id: sub.id,
            sold_out: Boolean(v.is_sold_out || sub.is_sold_out),
            sku: sub.sku || v.sku,
            stock: sub.variant_quantity ?? null,
          });
        }
      } else {
        out.push({
          id: v.id,
          name: label,
          price: base + add,
          variation_id: v.id,
          sub_variant_id: null,
          sold_out: Boolean(v.is_sold_out),
          sku: v.sku,
          stock: v.variation_quantity ?? v.quantity ?? v.stock,
          attributes: v.attributes,
        });
      }
    }
    return out;
  }
}

// -------------------------------------------------------
// Singleton
// -------------------------------------------------------

let globalClient: ApiClient | null = null;

export function getApiClient(options?: {
  merchantId: string | number;
  apiKey?: string;
  sandbox?: boolean;
  apiBase?: string;
  debug?: boolean;
}): ApiClient {
  if (!globalClient && options) {
    globalClient = new ApiClient(options);
  }
  if (!globalClient) {
    throw new Error('API client not initialized. Call with options first.');
  }
  return globalClient;
}

export function resetApiClient(): void {
  globalClient = null;
}
